/**
 * The PIN column of the report-page tables (Creators dashboard, Ad Ops
 * campaign and advertiser pages): the passcode in a fixed-width mono chip
 * (masked by default, so a screen share never shows it), show/hide and copy
 * as proper 28px icon buttons, and Reset PIN. A trivially guessed PIN left
 * over from before the server refused them is flagged.
 */

import React, { useState } from 'react';
import Button from './Button';
import IconButton from './IconButton';
import { WEAK_PINS } from '../../utils/reportPagePins';
import { CheckIcon, CopyIcon, EyeIcon, EyeOffIcon } from './icons';

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 64,
  boxSizing: 'border-box',
  padding: '4px 10px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cg-neutral-50)',
  border: '1px solid var(--border-1)',
  fontFamily: 'var(--font-mono)',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.18em',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--fg-1)',
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

interface PinCellProps {
  /** Row name (dashboard, campaign, advertiser), for the buttons' accessible names. */
  label: string;
  pin: string;
  copied: boolean;
  onCopy: () => void;
  onReset: () => void;
}

const PinCell: React.FC<PinCellProps> = ({ label, pin, copied, onCopy, onReset }) => {
  const [visible, setVisible] = useState(false);
  const weak = WEAK_PINS.has(pin);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
        <code style={chipStyle}>
          {visible ? pin : '••••'}
        </code>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="sm" label={`${visible ? 'Hide' : 'Show'} PIN for ${label}`} onClick={() => setVisible((v) => !v)}>
            {visible ? <EyeOffIcon /> : <EyeIcon />}
          </IconButton>
          <IconButton
            size="sm"
            label={`Copy PIN for ${label}`}
            onClick={onCopy}
            style={copied ? { color: 'var(--success)' } : undefined}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
          </IconButton>
        </span>
        <Button size="sm" variant="ghost" onClick={onReset}>
          Reset PIN
        </Button>
        <span aria-live="polite" style={visuallyHidden}>
          {copied ? 'PIN copied' : ''}
        </span>
      </div>
      {weak && <span style={{ fontSize: 11.5, color: 'var(--warning)' }}>Easily guessed PIN — reset it</span>}
    </div>
  );
};

export default PinCell;
